/**
 * Zod runtime validator for GameSpec v1.
 *
 * Used by:
 *  - DynamicGameRenderer (defensive — rejects malformed specs)
 *  - generate-ai-game edge fn (the ENGINE GATE)
 *
 * Keep in lock-step with src/types/gameSpec.ts and manifest.ts.
 */

import { z } from 'zod';
import { GAME_ENGINE, SCENE_TYPES, THEMES } from './manifest';
import type { GameSpec } from '@/types/gameSpec';

const L = GAME_ENGINE.limits;

const txt = (max: number) =>
  z.string().trim().min(1).max(max);

const sceneBase = {
  id: z.string().min(1).max(64),
  prompt: txt(L.text_field_max_chars),
  hint: txt(L.text_field_max_chars).nullish(),
  explanation: txt(L.text_field_max_chars).nullish(),
  points: z.number().int().min(0).max(1000).optional(),
};

const mcq = z.object({
  ...sceneBase,
  type: z.literal('mcq'),
  options: z.array(txt(L.text_field_max_chars)).min(L.options_min).max(L.options_max),
  correct_index: z.number().int().min(0),
}).refine((s) => s.correct_index < s.options.length, {
  message: 'correct_index out of range',
  path: ['correct_index'],
});

const fillBlank = z.object({
  ...sceneBase,
  type: z.literal('fill_blank'),
  template: txt(L.text_field_max_chars).refine((t) => t.includes('___'), {
    message: 'template must contain ___',
  }),
  accepted_answers: z.array(txt(80)).min(1).max(10),
  case_sensitive: z.boolean().optional(),
});

const flashcard = z.object({
  ...sceneBase,
  type: z.literal('flashcard'),
  front: txt(L.text_field_max_chars),
  back: txt(L.text_field_max_chars),
});

const matching = z.object({
  ...sceneBase,
  type: z.literal('matching'),
  pairs: z.array(z.object({
    left: txt(80),
    right: txt(80),
  })).min(2).max(L.matching_pairs_max),
});

const ordering = z.object({
  ...sceneBase,
  type: z.literal('ordering'),
  items: z.array(txt(120)).min(2).max(L.ordering_items_max),
});

const dragToBucket = z.object({
  ...sceneBase,
  type: z.literal('drag_to_bucket'),
  buckets: z.array(txt(40)).min(2).max(L.bucket_count_max),
  items: z.array(z.object({
    label: txt(80),
    bucket_index: z.number().int().min(0),
  })).min(2).max(20),
}).refine((s) => s.items.every((i) => i.bucket_index < s.buckets.length), {
  message: 'item.bucket_index out of range',
});

const numberLine = z.object({
  ...sceneBase,
  type: z.literal('number_line'),
  min: z.number(),
  max: z.number(),
  step: z.number().positive(),
  target: z.number(),
  tolerance: z.number().min(0).optional(),
}).refine((s) => s.max > s.min && s.target >= s.min && s.target <= s.max, {
  message: 'invalid number_line bounds/target',
});

const workedSteps = z.object({
  ...sceneBase,
  type: z.literal('worked_steps'),
  steps: z.array(z.object({
    text: txt(L.text_field_max_chars),
    check: txt(80).nullish(),
  })).min(1).max(8),
  final_answer: txt(80),
});

const reading = z.object({
  ...sceneBase,
  type: z.literal('reading'),
  passage: txt(2000),
  questions: z.array(z.object({
    question: txt(L.text_field_max_chars),
    options: z.array(txt(L.text_field_max_chars)).min(L.options_min).max(L.options_max),
    correct_index: z.number().int().min(0),
  })).min(1).max(6),
}).refine(
  (s) => s.questions.every((q) => q.correct_index < q.options.length),
  { message: 'question.correct_index out of range' },
);

const sprint = z.object({
  ...sceneBase,
  type: z.literal('sprint'),
  duration_seconds: z.number().int().min(15).max(300),
  questions: z.array(z.object({
    text: txt(L.text_field_max_chars),
    options: z.array(txt(80)).min(L.options_min).max(L.options_max),
    correct_index: z.number().int().min(0),
  })).min(3).max(L.sprint_questions_max),
}).refine(
  (s) => s.questions.every((q) => q.correct_index < q.options.length),
  { message: 'sprint question.correct_index out of range' },
);

const tapCorrect = z.object({
  ...sceneBase,
  type: z.literal('tap_correct'),
  items: z.array(z.object({
    label: txt(80),
    correct: z.boolean(),
  })).min(3).max(20),
}).refine((s) => s.items.some((i) => i.correct), {
  message: 'tap_correct must have ≥1 correct item',
});

const memoryPairs = z.object({
  ...sceneBase,
  type: z.literal('memory_pairs'),
  pairs: z.array(z.object({
    a: txt(40),
    b: txt(40),
  })).min(2).max(L.memory_pairs_max),
});

const sequenceInput = z.object({
  ...sceneBase,
  type: z.literal('sequence_input'),
  expected: z.array(txt(40)).min(1).max(12),
  case_sensitive: z.boolean().optional(),
});

// Using z.union (not discriminatedUnion) because some variants use .refine()
// which produces ZodEffects — incompatible with discriminatedUnion.
export const sceneSchema = z.union([
  mcq,
  fillBlank,
  flashcard,
  matching,
  ordering,
  dragToBucket,
  numberLine,
  workedSteps,
  reading,
  sprint,
  tapCorrect,
  memoryPairs,
  sequenceInput,
]);

export const gameSpecSchema = z.object({
  version: z.literal(1),
  meta: z.object({
    title: txt(L.title_max_chars),
    description: txt(L.description_max_chars),
    grade: z.string().min(1).max(8),
    subject_id: z.string().uuid().nullish(),
    category_id: z.string().uuid().nullish(),
    topic_id: z.string().uuid().nullish(),
    learning_objectives: z.array(txt(160)).max(8).default([]),
    skills_assessed: z.array(txt(80)).max(12).default([]),
    estimated_minutes: z.number().int().min(1).max(L.estimated_minutes_max),
  }),
  theme: z.object({
    palette: z.enum(THEMES),
    mascot: z.string().max(40).nullish(),
  }),
  rules: z.object({
    lives: z.number().int().min(1).max(L.lives_max),
    time_limit_seconds: z.number().int().min(15).max(L.time_limit_seconds_max).nullish(),
    scoring: z.object({
      correct: z.number().min(0).max(1000),
      wrong: z.number().max(0), // never positive
      bonus_streak: z.number().min(0).max(1000).optional(),
      time_bonus_per_sec: z.number().min(0).max(100).optional(),
      hint_penalty: z.number().max(0).optional(),
    }),
    pass_threshold: z.number().min(0).max(1),
    max_score: z.number().int().min(0).optional(), // server-computed
  }),
  scenes: z.array(sceneSchema).min(L.scenes_min).max(L.scenes_max),
});

export type ValidationResult =
  | { ok: true; spec: GameSpec }
  | { ok: false; errors: string[] };

export function validateGameSpec(input: unknown): ValidationResult {
  const parsed = gameSpecSchema.safeParse(input);
  if (parsed.success) {
    return { ok: true, spec: parsed.data as unknown as GameSpec };
  }
  return {
    ok: false,
    errors: parsed.error.issues.map(
      (i) => `${i.path.join('.') || '(root)'}: ${i.message}`,
    ),
  };
}

/** Re-export for AI prompt consumption. */
export { SCENE_TYPES, THEMES };
